import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { mahallaApi } from '../services/api';

const DEBOUNCE_MS = 300;

/** Mahalla tanlash: fuzzy qidiruv, 90%+ mos → avto; 2–3 ta → tanlash */
export default function MahallaSelector({ value, onChange, required = true, className = '' }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState(value?.name || '');
  const [status, setStatus] = useState(null);
  const [matches, setMatches] = useState([]);
  const [allMahallas, setAllMahallas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    mahallaApi.getAll().then(setAllMahallas).catch(() => {});
  }, []);

  useEffect(() => {
    if (value?.name && value.name !== query) setQuery(value.name);
  }, [value?.id, value?.name, query]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const applySelection = useCallback((m) => {
    onChange?.({ id: m.id, name: m.name });
    setQuery(m.name);
    setOpen(false);
    setStatus('SELECTED');
    setMatches([]);
  }, [onChange]);

  const runSearch = useCallback(async (text) => {
    const q = text.trim();
    if (!q) {
      setStatus('LIST');
      setMatches(allMahallas.map((m) => ({ id: m.id, name: m.name, score: 1 })));
      return;
    }
    setLoading(true);
    try {
      const res = await mahallaApi.search(q);
      setStatus(res.status);
      setMatches(res.matches || []);
      if (
        (res.status === 'EXACT' || res.status === 'SINGLE') &&
        res.suggestedId &&
        res.matches?.[0]
      ) {
        applySelection(res.matches[0]);
      }
    } catch {
      setStatus('NONE');
      setMatches([]);
    } finally {
      setLoading(false);
    }
  }, [allMahallas, applySelection]);

  const onInputChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    onChange?.(null);
    setStatus(null);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(text), DEBOUNCE_MS);
  };

  const displayList =
    status === 'LIST' || !query.trim()
      ? (matches.length ? matches : allMahallas.map((m) => ({ id: m.id, name: m.name, score: 1 })))
      : matches;

  const showPicker = open && displayList.length > 0 && status !== 'SELECTED';

  return (
    <div className={`relative ${className}`} ref={wrapRef}>
      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] px-1 mb-2 italic">
        {t('mahallaSelector.label')} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[20px] pointer-events-none z-10">
          location_on
        </span>
        <input
          type="text"
          value={query}
          onChange={onInputChange}
          onFocus={() => {
            setOpen(true);
            runSearch(query);
          }}
          placeholder={t('mahallaSelector.placeholder')}
          autoComplete="off"
          className={`w-full h-14 pl-12 pr-12 rounded-2xl border-2 bg-white dark:bg-slate-900 font-bold italic text-sm transition-all outline-none ${
            value?.id
              ? 'border-[#1e9f6b] text-[#1e9f6b] shadow-sm shadow-[#1e9f6b]/10'
              : 'border-slate-100 dark:border-slate-800 focus:border-[#1e9f6b]'
          }`}
        />
        {loading && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 animate-spin text-[20px]">
            progress_activity
          </span>
        )}
        {value?.id && !loading && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-[#1e9f6b] text-[22px]">
            check_circle
          </span>
        )}
      </div>

      {status === 'AMBIGUOUS' && matches.length > 0 && !value?.id && (
        <p className="mt-2 text-[10px] font-bold text-amber-600 uppercase tracking-widest px-1">
          {t('mahallaSelector.multipleMatches')}
        </p>
      )}

      {status === 'NONE' && query.trim() && !value?.id && (
        <p className="mt-2 text-[10px] font-bold text-red-500 uppercase tracking-widest px-1">
          {t('mahallaSelector.noMatches')}
        </p>
      )}

      {showPicker && (
        <ul className="absolute z-50 mt-2 w-full max-h-52 overflow-y-auto rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl py-1">
          {displayList.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                onClick={() => applySelection(m)}
                className={`w-full text-left px-4 py-3 text-sm font-bold transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/20 ${
                  value?.id === m.id ? 'text-[#1e9f6b] bg-emerald-50/50' : 'text-slate-700 dark:text-slate-200'
                }`}
              >
                {m.name}
                {m.score != null && m.score < 1 && (
                  <span className="ml-2 text-[10px] text-slate-400">{Math.round(m.score * 100)}%</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}